// Dependencies
import { useEffect } from "react";
import { useSelector } from "react-redux";

// Project Dependencies
import { getJobFileDownloadByJobName } from "../../selectors/jobs";

/**
 * @deprecated This hook is deprecated and should not be used for new implementations.
 *
 * Use the new async task framework instead, which provides:
 * - useUserAsyncTask hook for complete task management
 * - useAsyncTaskStream for real-time SSE progress updates
 * - DownloadButtonSSE component for download buttons with progress
 * - Type-safe task contracts
 * - Better performance without WebSocket polling
 *
 * Migration example:
 * ```typescript
 * // Old way (deprecated):
 * useAsyncJobDownload("export-company-meetings-csv");
 *
 * // New way:
 * import { useUserAsyncTask } from "../../hooks/useUserAsyncTask";
 * import { ASYNC_TASK_NAMES } from "@performyard/models";
 *
 * const { task, triggerTask } = useUserAsyncTask(
 *   ASYNC_TASK_NAMES.EXPORT_COMPANY_MEETINGS_CSV
 * );
 *
 * // Trigger the task
 * await triggerTask(() => exportCompanyMeetingsCsv({ filters }));
 *
 * // Access the result
 * if (task?.status === "completed" && task.metadata?.signedUrl) {
 *   window.open(task.metadata.signedUrl, "_blank");
 * }
 * ```
 *
 * See documentation: docs/guides/async-task-framework.md
 *
 * ---
 *
 * Given a job name, this hook will download the CSV file associated with that
 * job. This is useful for legacy html pages that need to download a CSV file
 * and can't use the DownloadButton component.
 */
export const useAsyncJobDownload = (jobName: string) => {
	const file = useSelector((state) => getJobFileDownloadByJobName(state, jobName));

	useEffect(() => {
		if (file) {
			window.open(file, "_blank");
		}
	}, [file]);

	return null;
};

/**
 * @deprecated Use the new async task framework hooks instead.
 * See useUserAsyncTask or useAsyncTaskStream.
 *
 * A component that will download the CSV file associated with the given job.
 * This is helpful for legacy class components that need access to the
 * useAsyncJobDownload hook.
 */
export const AsyncJobDownloadComponent = (props: { jobName: string }) => {
	useAsyncJobDownload(props.jobName);
	return null;
};
